import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

/* ============================================================================
   MenuAcoes - o menu "⋮" de cada linha de tabela.

   POR QUE EXISTE: a primeira rodada do visual novo trocou as tabelas e, no
   caminho, deixou de fora o menu de acoes de seis telas. Trocar o estilo nao e
   motivo para tirar funcao - o resultado foi que dava para VER os registros e nao
   dava para FAZER nada com eles. Este componente e para que a acao de linha seja
   uma peca so, presente por construcao, e nao algo que cada tela se lembre de
   colocar.

   Substitui o `TableMoreMenu` do MUI. Comportamentos que ele tinha e que
   precisavam continuar existindo:

   - Esc fecha, clique fora fecha.
   - So UM menu aberto por vez: o controle e do pai (`aberto` + `onAlternar`),
     porque com estado interno dois menus de linhas diferentes ficavam abertos
     juntos.
   - Acao destrutiva em vermelho e por ULTIMO, separada por uma linha. Apagar
     encostado em Editar e onde se erra o clique.

   ---------------------------------------------------------------------------
   🔴 DEFEITO CORRIGIDO (26/08/2026): "o menu dos 3 pontos nao abre na Busca".

   Ele ABRIA - e era cortado, o que na tela e a mesma coisa que nao abrir.

   O menu usava `position: absolute` dentro da celula, e o comentario antigo aqui
   dizia que isso funcionava porque nenhum ancestral tem `transform`. A conta
   estava incompleta: o que corta um elemento posicionado nao e so `transform`, e
   tambem `overflow`. A tabela da Busca tem TRES ancestrais que cortam:

     overflow-hidden   (a moldura do cartao)
     overflow-x-auto   (a rolagem horizontal das colunas)
     max-h-[58vh] overflow-y-auto   (a rolagem vertical das linhas)

   E `overflow-x: auto` corta na VERTICAL tambem: pelo CSS, quando um eixo e auto
   o outro deixa de ser `visible`. Com uma linha so na tabela, o menu abria 32px
   abaixo do botao - inteiro fora da caixa - e desaparecia.

   AGORA o menu vai para `document.body` por portal, com `position: fixed` medida
   a partir do retangulo do botao. Fora das caixas, nao ha o que o corte. A
   posicao e recalculada na rolagem (com captura, para pegar tambem a rolagem dos
   contêineres internos) e no redimensionamento, senao o menu ficaria parado
   enquanto a tabela rola por baixo dele.

   Vira para CIMA quando nao ha espaco abaixo, e nunca passa das bordas da janela.
   ---------------------------------------------------------------------------- */

export type AcaoLinha<T> = {
  rotulo: string;
  /** `true` pinta de vermelho e joga para o fim, depois de um separador. */
  perigo?: boolean;
  /** `false` esconde o item - use para permissao por perfil. */
  visivel?: boolean;
  ao: (linha: T) => void;
};

export default function MenuAcoes<T>({
  linha,
  acoes,
  aberto,
  onAlternar,
  onFechar,
  ancorarADireita,
  gatilho,
}: {
  linha: T;
  acoes: Array<AcaoLinha<T>>;
  aberto: boolean;
  onAlternar: () => void;
  onFechar: () => void;
  /** Use nas colunas do fim da tabela, para o menu nao sair da tela. */
  ancorarADireita?: boolean;
  /* CONTEUDO E APARENCIA DO BOTAO QUE ABRE (10/09/2026).

     Nasceu como os tres pontinhos da linha da tabela, e continua sendo isso
     por padrao. Mas a barra de acoes do dispositivo precisava de um "Mais
     acoes" com rotulo visivel, e a alternativa era um segundo componente de
     menu suspenso - com a mesma conta de posicao, o mesmo portal e o mesmo
     tratamento de rolagem que este ja resolve. Duas implementacoes de menu no
     mesmo produto divergem no primeiro ajuste. */
  gatilho?: { rotulo: React.ReactNode; className?: string; aria?: string };
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const refBotao = React.useRef<HTMLButtonElement | null>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number; alturaMax: number } | null>(null);

  const visiveis = acoes.filter((a) => a.visivel !== false);
  const normais = visiveis.filter((a) => !a.perigo);
  const perigosas = visiveis.filter((a) => a.perigo);

  const LARGURA = 196;
  const MARGEM = 8;

  /* Altura ESTIMADA pela contagem de itens (cada item ~30px, mais o padding e o
     separador). Serve so para decidir se cabe abaixo do botao; o `maxHeight`
     abaixo garante que o menu nunca passe da janela mesmo se a estimativa errar. */
  const alturaEstimada = 10 + visiveis.length * 30 + (perigosas.length && normais.length ? 9 : 0);

  const medir = React.useCallback(() => {
    const b = refBotao.current?.getBoundingClientRect();
    if (!b) return;

    const espacoAbaixo = window.innerHeight - b.bottom - MARGEM;
    const espacoAcima = b.top - MARGEM;
    const paraCima = espacoAbaixo < alturaEstimada && espacoAcima > espacoAbaixo;

    const esquerdaCrua = ancorarADireita ? b.right - LARGURA : b.left;

    setPos({
      top: paraCima ? Math.max(MARGEM, b.top - MARGEM - Math.min(alturaEstimada, espacoAcima)) : b.bottom + 4,
      left: Math.max(MARGEM, Math.min(esquerdaCrua, window.innerWidth - LARGURA - MARGEM)),
      alturaMax: Math.max(120, paraCima ? espacoAcima : espacoAbaixo),
    });
  }, [ancorarADireita, alturaEstimada]);

  React.useEffect(() => {
    if (!aberto) {
      setPos(null);
      return undefined;
    }

    medir();

    const aoClicar = (e: MouseEvent) => {
      const alvo = e.target as Node;
      // O botao entra na conta: sem isso, clicar nele com o menu aberto fechava
      // aqui e reabria no `onClick`, e o menu nunca fechava pelo proprio botao.
      if (ref.current?.contains(alvo) || refBotao.current?.contains(alvo)) return;
      onFechar();
    };
    const aoTeclar = (e: KeyboardEvent) => e.key === 'Escape' && onFechar();

    document.addEventListener('mousedown', aoClicar);
    window.addEventListener('keydown', aoTeclar);
    // `capture` para receber a rolagem dos contêineres internos da tabela, que
    // nao borbulha para a window.
    document.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);

    return () => {
      document.removeEventListener('mousedown', aoClicar);
      window.removeEventListener('keydown', aoTeclar);
      document.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [aberto, onFechar, medir]);

  if (!visiveis.length) return <span className="text-faint">—</span>;

  return (
    <span className="relative inline-flex">
      <button
        ref={refBotao}
        type="button"
        onClick={(e) => {
          // A linha inteira costuma navegar; sem isto, abrir o menu abriria o
          // cadastro junto.
          e.stopPropagation();
          onAlternar();
        }}
        className={
          gatilho?.className ??
          'grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-soft hover:text-ink'
        }
        aria-label={gatilho?.aria ?? 'Opções'}
        aria-haspopup="menu"
        aria-expanded={aberto}
      >
        {gatilho?.rotulo ?? '⋮'}
      </button>

      {aberto &&
        pos &&
        createPortal(
        <div
          ref={ref}
          role="menu"
          onClick={(e) => e.stopPropagation()}
          /* `fixed` + portal no body: e o que tira o menu dos contêineres com
             `overflow` da tabela, que o cortavam. z acima do modal (1400) porque
             ha tabela com este menu dentro de modal. */
          style={{ top: pos.top, left: pos.left, maxHeight: pos.alturaMax }}
          className="fixed z-[1500] w-[196px] overflow-y-auto rounded-lg border border-line bg-surface py-1 text-ink shadow-card"
        >
          {normais.map((a) => (
            <button
              key={a.rotulo}
              type="button"
              role="menuitem"
              onClick={() => {
                onFechar();
                a.ao(linha);
              }}
              className="block w-full px-3 py-1.5 text-left text-[12px] text-ink hover:bg-surface-2"
            >
              {a.rotulo}
            </button>
          ))}

          {!!perigosas.length && !!normais.length && <div className="my-1 h-px bg-line" />}

          {perigosas.map((a) => (
            <button
              key={a.rotulo}
              type="button"
              role="menuitem"
              onClick={() => {
                onFechar();
                a.ao(linha);
              }}
              className="block w-full px-3 py-1.5 text-left text-[12px] text-danger hover:bg-danger-soft"
            >
              {a.rotulo}
            </button>
          ))}
        </div>,
          document.body
        )}
    </span>
  );
}

/* ----------------------------------------------------------------------------
   Confirmacao de exclusao - o par natural do item "Apagar".

   Fica aqui, junto do menu, porque nao existe um sem o outro: todo lugar que
   oferece apagar precisa dizer O QUE vai apagar. "Tem certeza?" sem o nome do
   registro e como se apaga a linha errada.
   ---------------------------------------------------------------------------- */
export function ResumoExclusao({
  titulo,
  detalhe,
  extra,
}: {
  titulo: string;
  detalhe?: string;
  extra?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
      <div className="font-semibold">{titulo}</div>
      {detalhe && <div className="tabular-nums text-[11px] text-muted-foreground">{detalhe}</div>}
      {extra && <div className="mt-2 text-muted-foreground">{extra}</div>}
    </div>
  );
}
